'use client';

import { useMemo } from 'react';
import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useCellServiceSession } from '@/hooks/useCellServiceSession';
import { mobileGraphql, mobileKeys, type MobileDeviceOwner } from '@/shared/mobile-service/api/cors-mobile-api';
import {
  buildDeviceOwnerBatchQueries,
  parseDeviceOwnerBatch,
} from '../lib/build-device-owner-query';

export type DeviceOwnersByAcquisition = Map<number, MobileDeviceOwner[]>;

/**
 * Device-owner profiles for a page of acquisitions, keyed by acquisition id.
 *
 * `commonDeviceOwners` takes one acquisition at a time, so the ids are aliased
 * into batched documents (see `build-device-owner-query.ts`) and issued
 * concurrently — four requests for a 100-row page instead of a hundred.
 *
 * Chunks settle independently on purpose. `mobileGraphql` rejects whenever the
 * response carries a non-empty `errors` array, even alongside partial `data`,
 * so a single unresolvable acquisition would otherwise blank every owner column
 * on the page. Here it blanks its own chunk and the rest of the table fills in.
 */
export function useAcquisitionDeviceOwners(
  mobileAcquisitionIds: readonly number[],
): UseQueryResult<DeviceOwnersByAcquisition, Error> {
  const { cellId, accessToken, enabled } = useCellServiceSession();

  // Sorted so paging back to a cached page hits the same key regardless of the
  // order the rows arrived in.
  const ids = useMemo(
    () => [...new Set(mobileAcquisitionIds)].sort((a, b) => a - b),
    [mobileAcquisitionIds],
  );

  return useQuery<DeviceOwnersByAcquisition, Error>({
    queryKey: mobileKeys.acquisitionDeviceOwners(cellId, ids),
    enabled: enabled && ids.length > 0,
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      const documents = buildDeviceOwnerBatchQueries(ids);
      const settled = await Promise.allSettled(
        documents.map((query) =>
          mobileGraphql<Record<string, unknown>>({
            cellId: cellId as string,
            accessToken: accessToken as string,
            endpoint: 'mobile',
            query,
            signal,
          }),
        ),
      );

      const owners: DeviceOwnersByAcquisition = new Map();
      for (const result of settled) {
        if (result.status === 'fulfilled') parseDeviceOwnerBatch(result.value, owners);
      }
      return owners;
    },
  });
}
